/**
 * PageHeader Component
 * 
 * A header component for entity detail pages with icon, title, ID, status, and actions.
 * Used for System detail pages showing system info and action buttons.
 */

import React from 'react';
import { Box, Typography, Avatar, Chip, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import { colors, typography as typographyTokens, radii, spacing } from '../../tokens';

export interface PageHeaderProps {
  /** Page title */
  title: string;
  /** Entity ID or identifier */
  id?: string | number;
  /** Status indicator (e.g., "Last seen 2 months ago") */
  status?: string;
  /** Status color */
  statusColor?: 'success' | 'warning' | 'error' | 'neutral';
  /** Icon to display */
  icon?: React.ReactNode;
  /** Icon background color */
  iconBgColor?: string;
  /** Action buttons */
  actions?: React.ReactNode;
  /** Additional styles */
  sx?: SxProps<Theme>;
}

const statusColorMap = {
  success: colors.success.main,
  warning: colors.warning.main,
  error: colors.error.main,
  neutral: colors.neutral[500],
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  id,
  status,
  statusColor = 'neutral',
  icon,
  iconBgColor = colors.primary[100],
  actions,
  sx,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 3,
      mb: 3,
      ...sx,
    }}
  >
    {/* Left side: Icon, Title, Metadata */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      {/* Icon */}
      {icon && (
        <Avatar
          sx={{
            width: 56,
            height: 56,
            backgroundColor: iconBgColor,
            color: colors.primary[600],
            borderRadius: `${radii.lg}px`,
            '& svg': {
              fontSize: 28,
            },
          }}
        >
          {icon}
        </Avatar>
      )}

      {/* Title and Metadata */}
      <Box>
        <Typography
          variant="h2"
          sx={{
            fontFamily: typographyTokens.fontFamily.display,
            fontWeight: typographyTokens.fontWeight.bold,
            fontSize: typographyTokens.fontSize['2xl'],
            color: colors.neutral[900],
            lineHeight: 1.2,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>

        {/* ID and Status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mt: 0.5,
          }}
        >
          {id !== undefined && (
            <Typography
              variant="body2"
              sx={{
                color: colors.neutral[500],
                fontFamily: typographyTokens.fontFamily.mono,
                fontSize: typographyTokens.fontSize.sm,
              }}
            >
              ID: {id}
            </Typography>
          )}

          {status && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusColorMap[statusColor],
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: colors.neutral[600],
                  fontSize: typographyTokens.fontSize.sm,
                }}
              >
                {status}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>

    {/* Right side: Actions */}
    {actions && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {actions}
      </Box>
    )}
  </Box>
);

export default PageHeader;

